// AI 助手 — mirrors control/internal/service Assistant (AssistantInfo,
// AssistantThread) and model.Assistant*. A question's answer streams as
// server-sent events, which axios cannot read: chat() uses fetch.
import { ApiError, TOKEN_KEY, localizeError } from './client'
import { i18n } from '@/i18n'

export interface AssistantInfo {
  enabled: boolean
  /** What the Chinese and the English console call it; empty is their own name. */
  name?: string
  name_en?: string
  /** The mark: spark or pinwheel; whether it moves. */
  icon?: 'spark' | 'pinwheel' | 'cloud' | 'bubble' | 'cube' | 'orb'
  animated?: boolean
  /** Where the console offers it: the bottom right, the navbar, or both. */
  placement?: 'float' | 'navbar' | 'both'
  /** The navbar entry carries the name beside the mark. */
  nav_label?: boolean
  /** The floating button's shape. */
  launcher?: 'circle' | 'capsule'
  /** The input box's 跑马灯: a light round the border; the suggestions turning over inside. */
  input_glow?: boolean
  input_hints?: boolean

  /** The suggested questions, for a Chinese console and an English one. */
  suggestions?: string[]
  suggestions_en?: string[]
  /** 0 is no limit. */
  daily_limit: number
  used_today: number
}
export interface AssistantConversation {
  id: number
  title: string
  created_at: string
  updated_at: string
}
export type AssistantRole = 'user' | 'assistant'
export type AssistantStatus = 'ok' | 'stopped' | 'failed'
export interface AssistantMessage {
  id: number
  conversation_id: number
  role: AssistantRole
  content: string
  status: AssistantStatus
  /** A failed answer's reason: a server error code. */
  error?: string
  created_at: string
}
export interface AssistantThread {
  conversation: AssistantConversation
  messages: AssistantMessage[]
}

export interface ChatHandlers {
  meta?: (d: { conversation_id: number; title: string }) => void
  thinking?: () => void
  delta?: (text: string) => void
  done?: (d: { message_id: number; status: AssistantStatus }) => void
  /** The answer failed after it began: a localized sentence and its code. */
  error?: (e: { code: string; message: string }) => void
}

function auth(): Record<string, string> {
  const tok = localStorage.getItem(TOKEN_KEY)
  return tok ? { Authorization: `Bearer ${tok}` } : {}
}

async function json<T>(method: string, path: string): Promise<T> {
  const res = await fetch('/api/v1' + path, { method, headers: auth() })
  if (!res.ok) throw await failure(res)
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

async function failure(res: Response): Promise<ApiError> {
  let body: { code?: string; message?: string; params?: Record<string, unknown> } = {}
  try {
    body = await res.json()
  } catch {
    /* not JSON */
  }
  const code = body.code ?? `HTTP_${res.status}`
  return new ApiError(res.status, code, localizeError(body.code, body.message, body.params) || res.statusText, undefined, body.message)
}

export const assistantApi = {
  info: () => json<AssistantInfo>('GET', '/assistant'),
  conversations: () => json<{ items: AssistantConversation[] }>('GET', '/assistant/conversations').then((r) => r.items ?? []),
  thread: (id: number) => json<AssistantThread>('GET', `/assistant/conversations/${id}`),
  remove: (id: number) => json<void>('DELETE', `/assistant/conversations/${id}`),

  /**
   * Asks a question and calls the handlers as the answer comes. Rejects
   * with an ApiError when the question is refused before any answer (the
   * daily limit, another answer still coming); aborting the signal stops
   * the answer, which the server keeps as far as it went.
   */
  async chat(body: { conversation_id?: number; message: string }, h: ChatHandlers, signal: AbortSignal): Promise<void> {
    const res = await fetch('/api/v1/assistant/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...auth() },
      // The console's language: an English one is answered with its pages'
      // English names.
      body: JSON.stringify({ ...body, lang: i18n.global.locale.value }),
      signal,
    })
    if (!res.ok || !res.body) throw await failure(res)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      let cut: number
      while ((cut = buf.indexOf('\n\n')) >= 0) {
        const block = buf.slice(0, cut)
        buf = buf.slice(cut + 2)
        let event = 'message'
        let data = ''
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim()
          else if (line.startsWith('data:')) data += line.slice(5).trim()
        }
        // Comment lines (": ping") keep the stream alive and carry nothing.
        if (!data) continue
        let d: any
        try {
          d = JSON.parse(data)
        } catch {
          continue
        }
        if (event === 'meta') h.meta?.(d)
        else if (event === 'thinking') h.thinking?.()
        else if (event === 'delta') h.delta?.(String(d.text ?? ''))
        else if (event === 'done') h.done?.(d)
        else if (event === 'error') h.error?.({ code: d.code, message: localizeError(d.code, d.message, d.params) })
      }
    }
  },
}
